import React, { useEffect, useState } from 'react';
import { EditManagedKeyForm } from './EditManagedKeyForm';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { classNames } from '../../utilities/styleUtils';
import type { ISnowflakeAccount } from '../../interfaces/ISnowflakeAccountManagement';

interface EditAccountDialogProps {
  isOpen: boolean;
  account: ISnowflakeAccount | null;
  onClose: () => void;
  onSuccess: () => void;
  initialStep?: number;
}

/**
 * Modal dialog wrapper for the account editing flow
 *
 * Features:
 * - Modal overlay with semi-transparent background
 * - Dialog header with title "Edit Snowflake Account"
 * - Close button (X in top right)
 * - Contains the EditManagedKeyForm component
 * - Handles dialog open/close state
 * - Prevents closing during critical operations
 * - Responsive (full screen on mobile, dialog on desktop)
 *
 * @param isOpen - Whether dialog is open
 * @param account - The account to edit
 * @param onClose - Callback to close dialog
 * @param onSuccess - Callback when account is successfully updated
 */
export function EditAccountDialog({ isOpen, account, onClose, onSuccess, initialStep }: EditAccountDialogProps) {
  const [isProcessing, setIsProcessing] = useState(false);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key to close dialog
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isProcessing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isProcessing, onClose]);

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const handleSuccess = () => {
    setIsProcessing(false);
    onSuccess();
  };

  const handleCancel = () => {
    setIsProcessing(false);
    onClose();
  };

  if (!isOpen || !account) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Dialog Container */}
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6 md:p-8">
        {/* Dialog Panel */}
        <div
          className={classNames(
            'relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all',
            'max-h-[90vh] flex flex-col',
          )}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-900">Edit Snowflake Account</h2>
            <button
              type="button"
              className={classNames(
                'rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-dataops-primary-light-blue focus:ring-offset-2',
                isProcessing ? 'cursor-not-allowed opacity-50' : '',
              )}
              onClick={handleClose}
              disabled={isProcessing}
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <EditManagedKeyForm
              account={account}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
              initialStep={initialStep}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
